import type { ProgressApiFetch } from "../Progress/types";

// CO-9 — the athlete-scoped API seam for the coach detail surface. The shared
// Progress panels self-fetch through ProgressDeps.apiFetch; wrapping that seam
// with "append ?athleteSub=" is what lets the coach re-expose the athlete's own
// panels VERBATIM (same math, same render, same i18n) over the CO-11 read path.
//
// The allowlist is the safety property, not an optimisation: only routes that
// are actually edge-gated server-side may be scoped. A future panel that
// fetches an ungated path (e.g. /api/session-results/baselines) would silently
// read the COACH's own partition and show the wrong athlete's numbers — this
// wrapper throws instead, so the gap surfaces in dev the first time the panel
// mounts, never as silently-wrong data in front of a coach.
// /api/sessions carries CO-10's effort coverage (?include=sets): it is
// edge-gated by resolveCoachReadTarget and health-strips its sets on a
// cross-user read, while rir/rpe/fail survive (coachReadProjection is a
// denylist of the four session + two LogSet health fields). The isScopable
// guard below keeps this to the LIST route — GET /api/sessions/:id is
// genuinely self-only server-side and stays refused.
const SCOPABLE_PATHS = [
  "/api/session-results",
  // Listed in its OWN right, not as a sub-resource of the line above: each
  // entry matches only the bare path (see isScopable), so gating one route
  // can never silently open its siblings. This is the AlertsPanel read (CO-1)
  // — baselines carry per-muscle volume only, no health field, so the active
  // edge gates it exactly like the session-results list.
  "/api/session-results/baselines",
  "/api/exercises",
  "/api/sessions",
] as const;

// Each allowlisted path matches ONLY itself (bare, or with a query string) —
// never its sub-resources. A gated route earns its entry above one at a time;
// an ungated sibling (e.g. a future /api/session-results/export) stays refused
// by default instead of inheriting a parent's permission.
const isScopable = (path: string): boolean =>
  SCOPABLE_PATHS.some((p) => {
    if (!path.startsWith(p)) return false;
    const rest = path.slice(p.length);
    return rest === "" || rest.startsWith("?");
  });

export const athleteScopedPath = (path: string, athleteSub: string): string => {
  if (!isScopable(path)) {
    throw new Error(
      `athleteScopedApiFetch: path not on the coach read-path allowlist: ${path}`,
    );
  }
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}athleteSub=${encodeURIComponent(athleteSub)}`;
};

/**
 * Wrap a ProgressApiFetch so every allowlisted GET reads the assigned
 * athlete's partition (edge-gated server-side via resolveCoachReadTarget).
 * Non-GET or non-allowlisted calls throw — the coach detail surface is
 * read-only by construction.
 */
export const makeAthleteScopedApiFetch = (
  base: ProgressApiFetch,
  athleteSub: string,
): ProgressApiFetch => {
  const scoped = <T,>(
    path: string,
    opts?: Parameters<ProgressApiFetch>[1],
  ): Promise<T> => {
    if (opts?.method && opts.method !== "GET") {
      throw new Error(
        `athleteScopedApiFetch: read-only seam, refused ${opts.method} ${path}`,
      );
    }
    return base<T>(athleteScopedPath(path, athleteSub), opts);
  };
  return scoped;
};
